import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from '../contexts/AuthContextTest'
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  Shield, 
  Key,
  Save,
  Eye,
  EyeOff
} from 'lucide-react'
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function Profile() {
  const { user, updateProfile, changePassword } = useAuth()
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors } } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    }
  })

  const { register: registerPassword, handleSubmit: handlePasswordSubmit, formState: { errors: passwordErrors }, reset: resetPassword }  = useForm()

  const onProfileSubmit = async (data) => {
    const result = await updateProfile(data)
    if (result.success) {
      toast.success('Profile updated successfully!')
    }
  }

  const onPasswordSubmit = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    
    const result = await changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword
    })
    
    if (result.success) {
      setShowPasswordModal(false)
      resetPassword()
      toast.success('Password changed successfully!')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-secondary-900">Profile</h1>
        <p className="mt-1 text-base text-secondary-600">
          Manage your account information and settings
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Profile Information</h3>
              <p className="card-description">Update your personal information</p>
            </div>
            <div className="card-content">
              <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">First Name</label>
                    <input
                      {...registerProfile('firstName', { required: 'First name is required' })}
                      className="input"
                      placeholder="Enter first name"
                    />
                    {profileErrors.firstName && (
                      <p className="form-error">{profileErrors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Last Name</label>
                    <input
                      {...registerProfile('lastName', { required: 'Last name is required' })}
                      className="input"
                      placeholder="Enter last name"
                    />
                    {profileErrors.lastName && (
                      <p className="form-error">{profileErrors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Email</label>
                  <input
                    {...registerProfile('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    className="input"
                    placeholder="Enter email address"
                  />
                  {profileErrors.email && (
                    <p className="form-error">{profileErrors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Phone</label>
                  <input
                    {...registerProfile('phone')}
                    className="input"
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="flex justify-end">
                  <button type="submit" className="btn-primary">
                    <Save className="h-4 w-4 mr-2" />
                    Update Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Account Information</h3>
              <p className="card-description">Your account details</p>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-secondary-900">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-sm text-secondary-500">
                      {user?.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-secondary-100 rounded-full flex items-center justify-center">
                    <Shield className="h-5 w-5 text-secondary-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-secondary-900">
                      Role
                    </div>
                    <div className="text-sm text-secondary-500">
                      {user?.role?.name}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-secondary-100 rounded-full flex items-center justify-center">
                    <Building className="h-5 w-5 text-secondary-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-secondary-900">
                      Company
                    </div>
                    <div className="text-sm text-secondary-500">
                      {user?.company?.name || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-secondary-200">
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="btn-outline w-full"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={() => setShowPasswordModal(false)} />
            <div className="modal-content max-w-md">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Change Password
              </h3>
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
                <div>
                  <label className="label">Current Password</label>
                  <div className="relative">
                    <input
                      {...registerPassword('currentPassword', { required: 'Current password is required' })}
                      type={showCurrentPassword ? 'text' : 'password'}
                      className="input pr-10"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="form-error">{passwordErrors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">New Password</label>
                  <div className="relative">
                    <input
                      {...registerPassword('newPassword', { 
                        required: 'New password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                      type={showNewPassword ? 'text' : 'password'}
                      className="input pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <p className="form-error">{passwordErrors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Confirm New Password</label>
                  <div className="relative">
                    <input
                      {...registerPassword('confirmPassword', { required: 'Please confirm your new password' })}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="input pr-10"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p className="form-error">{passwordErrors.confirmPassword.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile
